import React from "react";
import { SearchIcon } from "lucide-react";
import { Checkbox } from "@component/checkbox";
import { EmptyState } from "@component/empty-state";
import { Applicant } from "@type/applicant";
import { Job } from "@type/job";
import LoadingBox from "@component/loading-box";
import UploadApplicantItem from "@component/upload-applicant-item";

interface Props {
    isLoading: boolean;
    applicants: Applicant[];
    job: Job | null;
    isAllSelected: boolean;
    sortDirection: string;
    shortlistedCountMap: Record<string, number>;
    commentCountMap: Record<string, number>;
    onSelectAllApplicants: (checked: boolean) => void;
    onSortDirection: (field: string, direction: "asc" | "desc" | "") => void;
    onSortBy: (field: string) => React.ReactNode;
    onViewProfile: (applicant: Applicant) => void;
    onViewReview?: (applicant: Applicant) => void;
    onSelectedApplicant: (applicantID: string, isSelected: boolean) => void;
    onUpdateScoreSimilarity: (applicantID: string, score: number) => void;
    onViewCV: (applicant: Applicant) => void;
    onReload: () => void;
    onApplicantStatusChange?: (applicantID: string, status: string) => void;
    onDeleteCandidate?: (applicant: Applicant) => void;
}

export default function TableListUpload({
    isLoading,
    applicants,
    job,
    isAllSelected,
    sortDirection,
    shortlistedCountMap,
    commentCountMap,
    onSelectAllApplicants,
    onSortDirection,
    onSortBy,
    onViewProfile,
    onViewReview,
    onSelectedApplicant,
    onUpdateScoreSimilarity,
    onViewCV,
    onReload,
    onApplicantStatusChange,
    onDeleteCandidate,
}: Props) {
    return (
        <table className="w-full">
            <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500">
                        <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={onSelectAllApplicants}
                            aria-label="Select all applicants"
                        />
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("candidate", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <div className="flex flex-row gap-2 justify-start items-center user-select-none cursor-pointer">
                            <span className="user-select-none">Candidate</span>
                            {onSortBy("candidate")}
                        </div>
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("location", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        Phone Number
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                        Email
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("created_at", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                            <span className="user-select-none">Uploaded Date</span>
                            {onSortBy("created_at")}
                        </div>
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        onClick={() => onSortDirection("status", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <div className="flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer">
                            <span className="user-select-none">Status</span>
                            {onSortBy("status")}
                        </div>
                    </th>
                    <th
                        className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex flex-row gap-2 justify-center items-center user-select-none cursor-pointer"
                        onClick={() => onSortDirection("similarity_score", sortDirection === "asc" ? "desc" : "asc")}
                    >
                        <span className="user-select-none">Match</span>
                        {onSortBy("similarity_score")}
                    </th>
                    <th className="h-[45px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <span className="user-select-none">Insights</span>
                    </th>
                    <th className="h-[45px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <span className="user-select-none">&nbsp;</span>
                    </th>
                    <th className="h-[45px] px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                        <span className="user-select-none">&nbsp;</span>
                    </th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {isLoading ? (
                    <tr>
                        <td colSpan={10}>
                            <LoadingBox />
                        </td>
                    </tr>
                ) : (
                    <>
                        {applicants.length > 0 ? (
                            applicants.map((applicant, index) => (
                                <UploadApplicantItem
                                    key={index}
                                    job={job}
                                    applicant={applicant}
                                    shortlistedCount={shortlistedCountMap[applicant.applicant_id] ?? 0}
                                    commentCount={commentCountMap[applicant.id] ?? 0}
                                    onViewProfile={() => onViewProfile(applicant)}
                                    onViewReview={onViewReview ? () => onViewReview(applicant) : undefined}
                                    onSelectedApplicant={onSelectedApplicant}
                                    onUpdateScoreSimilarity={onUpdateScoreSimilarity}
                                    onViewCV={onViewCV}
                                    onReload={onReload}
                                    onApplicantStatusChange={onApplicantStatusChange}
                                    onDeleteCandidate={onDeleteCandidate}
                                />
                            ))
                        ) : (
                            <tr>
                                <td colSpan={10} className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                    <EmptyState
                                        icon={<SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                                                title="No candidates yet."
                                        description={
                                            <>
                                                Candidates will appear here once you upload their CVs.
                                            </>
                                        }
                                    />
                                </td>
                            </tr>
                        )}
                    </>
                )}
            </tbody>
        </table>
    );
}
